/**
 * Event card media: show image when no video; show video when data-url is set.
 * data-url supports: YouTube (youtu.be/ID, youtube.com/watch?v=ID), Vimeo (vimeo.com/ID), MP4 (.mp4 URL).
 */

import { appendVimeoEmbed, createYoutubeEmbed } from "../utils/video-embeds.js";

const VIDEO_TYPE = { YOUTUBE: "youtube", VIMEO: "vimeo", MP4: "mp4" };

const EMBED_CLASS = "event-card__video-embed";

function getVideoType(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim().toLowerCase();
  if (u.endsWith(".mp4") || u.includes(".mp4?")) return VIDEO_TYPE.MP4;
  if (u.includes("youtube.com") || u.includes("youtu.be")) return VIDEO_TYPE.YOUTUBE;
  if (u.includes("vimeo.com")) return VIDEO_TYPE.VIMEO;
  return null;
}

function getYoutubeVideoId(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.trim().match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

function getVimeoVideoId(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.trim().match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function initEventCardMedia(block) {
  const container = block.querySelector("[data-url]");
  const url = container?.getAttribute("data-url")?.trim();
  const type = getVideoType(url);

  if (!type || !container) return;

  block.classList.add("event-card__media-block--has-video");
  container.classList.add("event-card__video");

  if (type === VIDEO_TYPE.YOUTUBE) {
    const id = getYoutubeVideoId(url);
    if (id) {
      container.classList.add("event-card__video--embed");
      createYoutubeEmbed(container, id, EMBED_CLASS);
    }
    return;
  }

  if (type === VIDEO_TYPE.VIMEO) {
    const id = getVimeoVideoId(url);
    if (id) {
      container.classList.add("event-card__video--embed");
      appendVimeoEmbed(container, id, EMBED_CLASS);
    }
    return;
  }

  if (type === VIDEO_TYPE.MP4) {
    const video = document.createElement("video");
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    const source = document.createElement("source");
    source.src = url;
    source.type = "video/mp4";
    video.appendChild(source);
    container.classList.add("event-card__video--mp4");
    container.appendChild(video);
  }
}

export function eventCardMedia() {
  document.querySelectorAll(".event-card__media-block").forEach(initEventCardMedia);
}
